import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'godo-disabled-accounts';

function loadDisabledBySegment() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { wh: [], stp: [] };
    const parsed = JSON.parse(raw);
    return {
      wh: Array.isArray(parsed.wh) ? parsed.wh.map(Number).filter(Boolean) : [],
      stp: Array.isArray(parsed.stp) ? parsed.stp.map(Number).filter(Boolean) : [],
    };
  } catch {
    return { wh: [], stp: [] };
  }
}

function saveDisabledBySegment(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/** Per-segment hidden account logins (WH / STP), persisted in localStorage. */
export function useDisabledAccounts(segment) {
  const [bySegment, setBySegment] = useState(loadDisabledBySegment);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setBySegment(loadDisabledBySegment());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const disabledSet = useMemo(
    () => new Set(bySegment[segment] || []),
    [bySegment, segment]
  );

  const persist = useCallback((next) => {
    setBySegment(next);
    saveDisabledBySegment(next);
  }, []);

  const disableLogin = useCallback(
    (login) => {
      const lg = Number(login);
      if (!lg) return;
      persist({
        ...bySegment,
        [segment]: [...new Set([...(bySegment[segment] || []), lg])],
      });
    },
    [bySegment, segment, persist]
  );

  const enableLogin = useCallback(
    (login) => {
      const lg = Number(login);
      if (!lg) return;
      persist({
        ...bySegment,
        [segment]: (bySegment[segment] || []).filter((id) => id !== lg),
      });
    },
    [bySegment, segment, persist]
  );

  const disabledLogins = useMemo(() => [...disabledSet].sort((a, b) => a - b), [disabledSet]);

  return {
    disabledSet,
    disabledLogins,
    disableLogin,
    enableLogin,
    disabledCount: disabledSet.size,
  };
}
